// src/services/agentService.js
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebase.js";

export const fetchCoreByRole = async (role) => {
  const q = query(collection(db, "coreAgents"), where("role", "==", role));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/** Returns Set of activated agent ids for current user */
export const fetchActiveAgents = async (uid) => {
  const snap = await getDocs(collection(db, "users", uid, "agents"));
  return new Set(snap.docs.map((d) => d.id));
};

/**
 * Returns every coach/coordinator the *user* actually has.
 * Merges static fields from coreAgents when id matches.
 */
export const fetchUserAgents = async (uid) => {
  const [userSnap, coreSnap] = await Promise.all([
    getDocs(collection(db, "users", uid, "agents")),
    getDocs(collection(db, "coreAgents")),
  ]);

  const coreMap = {};
  coreSnap.docs.forEach((d) => (coreMap[d.id] = { id: d.id, ...d.data() }));

  return userSnap.docs.map((d) => ({
    id: d.id,
    ...coreMap[d.id], // static catalogue fields (may be undefined)
    ...d.data(), // user‑specific overrides (spacing, streak…)
  }));
};

/**
 * Ensures every coordinator has a document under users/{uid}/agents.
 * Creates a minimal doc if missing so the user can chat with them.
 */
export const ensureCoordinatorDrafts = async (uid) => {
  const coordinators = await fetchCoreByRole("coordinator");
  for (const c of coordinators) {
    const ref = doc(db, "users", uid, "agents", c.id);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      let dataToSet = {
        activatedAt: serverTimestamp(),
        draft: false,
      };
      if (c.systemPrompt && c.userPrompt) {
        dataToSet.systemPrompt = c.systemPrompt;
        dataToSet.userPrompt = c.userPrompt;
      } else if (c.instructions) {
        dataToSet.instructions = c.instructions;
      }
      await setDoc(ref, dataToSet, { merge: true });
    } else {
      // Document exists, check if prompts or instructions need to be updated (Step 1 already implies this context)
      let dataToMerge = {}; // Step 1: Initialize dataToMerge
      const agentData = snap.data();

      if (c.systemPrompt && c.userPrompt) {
        // Step 2: Check for coreAgent's systemPrompt and userPrompt
        // Step 2.If yes:
        dataToMerge.systemPrompt = c.systemPrompt;
        dataToMerge.userPrompt = c.userPrompt;
        dataToMerge.instructions = ""; // Clear out old combined instructions
      } else {
        // Step 3: Else (if c.systemPrompt and c.userPrompt are NOT BOTH present on c)
        // Step 3.Check if c.instructions exists AND c.instructions is different from snap.data().instructions
        if (c.instructions && agentData.instructions !== c.instructions) {
          // Step 3.If both conditions are true:
          dataToMerge.instructions = c.instructions;
        }
      }

      // Step 4: After these conditions, if Object.keys(dataToMerge).length > 0
      if (Object.keys(dataToMerge).length > 0) {
        await setDoc(ref, dataToMerge, { merge: true });
      }
    }
  }
};

/**
 * Ensures the image generation agent exists for the user.
 * Creates a minimal doc if missing.
 */
// export const ensureImageGenerationAgent = async (uid) => {
//   const IMAGE_AGENT_ID = "image_generator";
//   const ref = doc(db, "users", uid, "agents", IMAGE_AGENT_ID);
//   const snap = await getDoc(ref);
//   if (!snap.exists()) {
//     const coreSnap = await getDoc(doc(db, "coreAgents", IMAGE_AGENT_ID));
//     const data = {
//       activatedAt: serverTimestamp(),
//       draft: false,
//     };
//     if (coreSnap.exists()) {
//       const c = coreSnap.data();
//       if (c.systemPrompt && c.userPrompt) {
//         data.systemPrompt = c.systemPrompt;
//         data.userPrompt = c.userPrompt;
//       } else if (c.instructions) {
//         data.instructions = c.instructions;
//       }
//     }
//     await setDoc(ref, data, { merge: true });
//   }
// };
